export const convertFilesToBase64 = (files) => {
  const promises = files.map((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve({ name: file.name, base64: reader.result, type: file.type });
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  });
  return Promise.all(promises);
};


export const base64ToFile = (base64String, filename = 'file') => {
  try {
    const [mimePart, base64Data] = base64String.split(',');
    
    if (!mimePart || !base64Data) {
      throw new Error('Invalid Base64 string format');
    }

    const mimeTypeMatch = mimePart.match(/:(.*?);/);
    if (!mimeTypeMatch) {
      throw new Error('MIME type could not be determined');
    }
    const mimeType = mimeTypeMatch[1];

    const byteString = atob(base64Data);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([arrayBuffer], { type: mimeType });

    const extension = mimeType.split('/').pop() || '';
    const finalFilename = filename.includes('.') ? filename : `${filename}.${extension}`;
    
    const file = new File([blob], finalFilename, { type: mimeType });
    return file;
  } catch (error) {
    console.error('Error converting Base64 to file:', error.message);
    throw error;
  }
};

/**
 * Get presigned upload URLs for files
 * @param {File[]} files - Array of File objects
 * @param {string} folder - Folder name in S3 (e.g., "portfolio")
 * @param {Function} axiosInstance - Axios instance with auth headers
 * @returns {Promise<Array<{upload_url: string, s3_key: string, filename: string, content_type: string}>>}
 */
export const getPresignedUploadUrls = async (files, folder, axiosInstance) => {
  try {
    // Validate inputs
    if (!files || files.length === 0) {
      throw new Error('Files array is required and cannot be empty');
    }
    if (!folder) {
      throw new Error('Folder is required');
    }
    if (!axiosInstance) {
      throw new Error('Axios instance is required');
    }
    
    // Validate all files are File objects
    const invalidFiles = files.filter(file => !(file instanceof File));
    if (invalidFiles.length > 0) {
      throw new Error(`Invalid files detected. All items must be File objects. Found ${invalidFiles.length} invalid file(s)`);
    }
    
    console.log('getPresignedUploadUrls called with:', {
      fileCount: files.length,
      folder,
      files: files.map(f => ({ name: f.name, type: f.type, size: f.size })),
    });
    
    // Use batch endpoint if multiple files, otherwise single endpoint
    if (files.length === 1) {
      const file = files[0];
      const response = await axiosInstance.post('/media/presigned-upload', {
        folder: folder,
        filename: file.name,
        content_type: file.type,
      });
      
      // Axios interceptor returns response.data directly, so response is already the data
      // Response uses camelCase: uploadUrl, s3Key, contentType
      const uploadUrl = response.uploadUrl || response.upload_url;
      const s3Key = response.s3Key || response.s3_key;
      const contentType = response.contentType || response.content_type || file.type;
      
      if (!uploadUrl) {
        console.error('Presigned URL response:', response);
        throw new Error('uploadUrl not found in presigned URL response');
      }
      
      if (!s3Key) {
        console.error('Presigned URL response:', response);
        throw new Error('s3Key not found in presigned URL response');
      }
      
      return [{
        upload_url: uploadUrl,
        s3_key: s3Key,
        filename: file.name,
        content_type: contentType, // Use contentType from response to match signature
      }];
    } else {
      // Batch endpoint - expects { "files": [...] } with contentType (camelCase)
      const filesData = {
        files: files.map(file => ({
          folder: folder,
          filename: file.name,
          contentType: file.type, // camelCase for batch endpoint
        }))
      };
      
      console.log('Batch presigned upload request:', filesData);
      const response = await axiosInstance.post('/media/presigned-upload/batch', filesData);
      
      // Log the full response for debugging
      console.log('Batch presigned upload response:', response);
      console.log('Response type:', typeof response);
      console.log('Is array:', Array.isArray(response));
      console.log('Response keys:', response && typeof response === 'object' ? Object.keys(response) : 'N/A');
      
      // Axios interceptor returns response.data directly
      // Response should be an array of {upload_url, s3_key} or wrapped in response.files
      let results = [];
      if (Array.isArray(response)) {
        results = response;
        console.log('Using response as array directly');
      } else if (response.files && Array.isArray(response.files)) {
        results = response.files;
        console.log('Using response.files');
      } else if (response.items && Array.isArray(response.items)) {
        results = response.items;
        console.log('Using response.items');
      } else if (response.data && Array.isArray(response.data)) {
        results = response.data;
        console.log('Using response.data');
      } else if (response.results && Array.isArray(response.results)) {
        results = response.results;
        console.log('Using response.results');
      } else if (response.uploads && Array.isArray(response.uploads)) {
        results = response.uploads;
        console.log('Using response.uploads');
      }
      
      if (!results || results.length === 0) {
        console.error('Batch presigned URL response structure:', {
          response,
          responseType: typeof response,
          isArray: Array.isArray(response),
          keys: response && typeof response === 'object' ? Object.keys(response) : null,
        });
        throw new Error('No presigned URLs returned from batch endpoint. Response structure: ' + JSON.stringify(response, null, 2));
      }
      
      if (results.length !== files.length) {
        console.error('Mismatch: requested', files.length, 'files but got', results.length, 'presigned URLs');
        throw new Error(`Expected ${files.length} presigned URLs but got ${results.length}`);
      }
      
      return results.map((item, index) => {
        // Response uses camelCase: uploadUrl, s3Key, contentType
        const uploadUrl = item.uploadUrl || item.upload_url;
        const s3Key = item.s3Key || item.s3_key;
        const contentType = item.contentType || item.content_type || files[index].type;
        
        if (!uploadUrl) {
          console.error('Item missing uploadUrl:', item);
          throw new Error(`uploadUrl not found for file ${files[index].name}`);
        }
        
        if (!s3Key) {
          console.error('Item missing s3Key:', item);
          throw new Error(`s3Key not found for file ${files[index].name}`);
        }
        
        return {
          upload_url: uploadUrl,
          s3_key: s3Key,
          filename: files[index].name,
          content_type: contentType, // Use contentType from response to match signature
        };
      });
    }
  } catch (error) {
    console.error('Error getting presigned URLs:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response,
      responseData: error.response?.data,
      responseStatus: error.response?.status,
      responseStatusText: error.response?.statusText,
      stack: error.stack,
    });
    
    // Provide more detailed error message
    let errorMessage = 'Failed to get presigned URLs';
    if (error.response?.data?.message) {
      errorMessage += `: ${error.response.data.message}`;
    } else if (error.response?.data?.detail) {
      errorMessage += `: ${error.response.data.detail}`;
    } else if (error.response?.data?.error) {
      errorMessage += `: ${error.response.data.error}`;
    } else if (error.message) {
      errorMessage += `: ${error.message}`;
    }
    
    // Include status code if available
    if (error.response?.status) {
      errorMessage += ` (Status: ${error.response.status})`;
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Upload a file to S3 using a presigned URL
 * Uses XMLHttpRequest instead of fetch to avoid CORS preflight issues
 * @param {File} file - File object to upload
 * @param {string} uploadUrl - Presigned upload URL from getPresignedUploadUrls
 * @param {string} contentType - Content type of the file
 * @returns {Promise<void>}
 */
export const uploadFileToS3 = async (file, uploadUrl, contentType) => {
  try {
    // Validate upload URL
    if (!uploadUrl) {
      throw new Error('Upload URL is undefined or empty');
    }
    
    // Ensure upload URL is an absolute URL
    if (!uploadUrl.startsWith('http://') && !uploadUrl.startsWith('https://')) {
      throw new Error(`Invalid upload URL format: ${uploadUrl}. URL must be absolute (start with http:// or https://)`);
    }
    
    // Use the file's MIME type (file.type) as the Content-Type header
    // This MUST match the content_type that was sent when requesting the presigned URL
    // Priority: file.type (actual file MIME type) > contentType from response > fallback
    const fileMimeType = file.type || contentType || 'application/octet-stream';
    
    // Verify the MIME type matches what was used in presigned URL generation
    if (contentType && file.type && file.type !== contentType) {
      console.warn(`MIME type mismatch: file.type=${file.type}, response.contentType=${contentType}. Using file.type`);
    }
    
    console.log('Uploading file to S3:', {
      filename: file.name,
      fileType: file.type,
      contentType: contentType,
      usingMimeType: fileMimeType,
      uploadUrl: uploadUrl.substring(0, 100) + '...', // Log first 100 chars only
    });
    
    // Use XMLHttpRequest instead of fetch to avoid CORS preflight issues
    // XMLHttpRequest handles S3 presigned URLs better than fetch
    // IMPORTANT: Content-Type header MUST match exactly what was used in presigned URL generation
    // The presigned URL signature includes content-type in X-Amz-SignedHeaders
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('PUT', uploadUrl, true);
      // Set Content-Type header with file's MIME type
      // This MUST match the content-type used when generating the presigned URL
      xhr.setRequestHeader('Content-Type', fileMimeType);
      
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('File uploaded successfully to S3:', file.name);
          resolve();
        } else {
          const error = new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`);
          console.error('S3 upload error:', {
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: xhr.responseText,
          });
          reject(error);
        }
      };
      
      xhr.onerror = () => {
        // Check if this is a CORS error
        const isCorsError = xhr.status === 0 ||
          (xhr.responseText && xhr.responseText.includes('CORS')) ||
          (xhr.statusText && xhr.statusText.includes('CORS'));

        // Presigned uploads may target AWS S3 or the E2E Object Store
        // (*.e2enetworks.net) depending on backend storage config;
        // both buckets must allow PUT from this origin (E2E CORS is portal-managed).
        const errorMessage = isCorsError
          ? 'CORS error: the storage bucket is not configured to allow uploads from this origin. Please contact backend team to configure CORS on the storage bucket (S3 or E2E Object Store).'
          : 'Network error during storage upload';

        const error = new Error(errorMessage);
        console.error('Network error uploading to storage:', {
          filename: file.name,
          status: xhr.status,
          statusText: xhr.statusText,
          uploadUrl: uploadUrl.substring(0, 100) + '...',
          isCorsError: isCorsError,
          note: isCorsError ? 'Storage bucket CORS configuration required. Bucket must allow PUT requests from origin.' : undefined,
        });
        reject(error);
      };
      
      xhr.ontimeout = () => {
        const error = new Error('Upload timeout');
        console.error('Upload timeout for file:', file.name);
        reject(error);
      };
      
      // Set a reasonable timeout (5 minutes)
      xhr.timeout = 5 * 60 * 1000;
      
      // Send the file
      xhr.send(file);
    });
  } catch (error) {
    console.error('Error uploading file to S3:', {
      filename: file.name,
      uploadUrl: uploadUrl,
      error: error.message,
    });
    throw new Error(`Failed to upload file to S3: ${error.message}`);
  }
};

/**
 * Upload multiple files to S3 using presigned URLs
 * @param {Array<{file: File, upload_url: string, content_type: string}>} uploadData - Array of upload data
 * @returns {Promise<void>}
 */
export const uploadFilesToS3 = async (uploadData) => {
  const uploadPromises = uploadData.map(({ file, upload_url, content_type }) =>
    uploadFileToS3(file, upload_url, content_type)
  );
  
  await Promise.all(uploadPromises);
};

/**
 * Get presigned download URL for a single S3 key
 * @param {string} s3Key - S3 key of the file to download/view
 * @param {Function} axiosInstance - Axios instance with auth headers
 * @returns {Promise<{download_url: string, s3_key: string, expires_in: number}>}
 */
export const getPresignedDownloadUrl = async (s3Key, axiosInstance) => {
  try {
    if (!s3Key) {
      throw new Error('S3 key is required');
    }

    const response = await axiosInstance.post('/media/presigned-download', {
      s3_key: s3Key,
    });

    // Axios interceptor returns response.data directly
    const downloadUrl = response.download_url || response.downloadUrl;
    const returnedS3Key = response.s3_key || response.s3Key;
    const expiresIn = response.expires_in || response.expiresIn;

    if (!downloadUrl) {
      console.error('Presigned download URL response:', response);
      throw new Error('download_url not found in presigned download URL response');
    }

    return {
      download_url: downloadUrl,
      s3_key: returnedS3Key || s3Key,
      expires_in: expiresIn || 600, // Default to 600 seconds (10 minutes)
    };
  } catch (error) {
    console.error('Error getting presigned download URL:', error);
    throw new Error(`Failed to get presigned download URL: ${error.response?.data?.message || error.message}`);
  }
};

/**
 * Filter and validate S3 keys - remove keys that could cause backend 500 (base64, data URLs, invalid format)
 * @param {string[]} s3Keys - Raw S3 keys from API
 * @returns {string[]} - Valid S3 keys safe to send to presigned endpoint
 */
export const filterValidS3Keys = (s3Keys) => {
  if (!s3Keys || !Array.isArray(s3Keys)) return [];
  return s3Keys.filter((key) => {
    if (!key || typeof key !== 'string') return false;
    const trimmed = key.trim();
    if (!trimmed || trimmed.length > 1024) return false;
    // Skip base64-like content (image data stored as "key")
    if (trimmed.startsWith('/9j/') || trimmed.startsWith('iVBORw0KGgo') || trimmed.startsWith('data:')) return false;
    if (/^[A-Za-z0-9+/=]{500,}$/.test(trimmed)) return false; // Long base64-ish string
    return true;
  });
};

/**
 * Get presigned download URLs for multiple S3 keys (batch)
 * Falls back to single-key requests when batch fails (e.g. 500) to avoid breaking the page.
 * @param {string[]} s3Keys - Array of S3 keys to get download URLs for
 * @param {Function} axiosInstance - Axios instance with auth headers
 * @returns {Promise<Array<{download_url: string|null, s3_key: string, expires_in: number}>>}
 *   `download_url` may be null when the object is missing from S3 or the API cannot sign it.
 */
export const getPresignedDownloadUrls = async (s3Keys, axiosInstance) => {
  if (!s3Keys || s3Keys.length === 0) {
    throw new Error('S3 keys array is required and cannot be empty');
  }

  const validKeys = filterValidS3Keys(s3Keys);
  if (validKeys.length === 0) {
    return [];
  }

  const tryBatch = async () => {
    const requestBody = { s3_keys: validKeys };
    const response = await axiosInstance.post('/media/presigned-download/batch', requestBody);

    let results = [];
    if (Array.isArray(response)) {
      results = response;
    } else if (response?.downloads && Array.isArray(response.downloads)) {
      results = response.downloads;
    } else if (response?.urls && Array.isArray(response.urls)) {
      results = response.urls;
    } else if (response?.items && Array.isArray(response.items)) {
      results = response.items;
    } else if (response?.data && Array.isArray(response.data)) {
      results = response.data;
    } else if (response?.s3_keys && Array.isArray(response.s3_keys)) {
      results = response.s3_keys;
    }

    if (!results || results.length === 0) {
      throw new Error('No presigned download URLs returned from batch endpoint');
    }

    if (results.length !== validKeys.length) {
      console.warn(
        `[getPresignedDownloadUrls] Expected ${validKeys.length} batch entries, got ${results.length}; mapping by index.`
      );
    }

    return validKeys.map((expectedKey, index) => {
      const item = results[index] || {};
      const downloadUrl = item.download_url || item.downloadUrl || item.url || null;
      const s3Key = item.s3_key || item.s3Key || item.key || expectedKey;
      const expiresIn = item.expires_in || item.expiresIn || 600;
      if (!downloadUrl) {
        console.warn(
          `[getPresignedDownloadUrls] No download_url for key "${expectedKey}" (object may be missing from bucket).`
        );
      }
      return { download_url: downloadUrl, s3_key: s3Key, expires_in: expiresIn };
    });
  };

  const trySingleKeyFallback = async () => {
    const results = [];
    for (let i = 0; i < validKeys.length; i++) {
      try {
        const r = await getPresignedDownloadUrl(validKeys[i], axiosInstance);
        results.push({ ...r, s3_key: r.s3_key || validKeys[i] });
      } catch (err) {
        console.warn(`Presigned URL failed for key ${i + 1}/${validKeys.length}:`, err?.message || err);
      }
    }
    return results;
  };

  try {
    if (validKeys.length === 1) {
      const result = await getPresignedDownloadUrl(validKeys[0], axiosInstance);
      return [{ ...result, s3_key: result.s3_key || validKeys[0] }];
    }

    try {
      return await tryBatch();
    } catch (batchErr) {
      const status = batchErr?.response?.status;
      const isServerError = status >= 500 || status === 408;
      if (isServerError) {
        console.warn('Batch presigned download failed (likely 500), falling back to single-key requests:', batchErr?.message);
        return await trySingleKeyFallback();
      }
      throw new Error(`Failed to get presigned download URLs: ${batchErr.response?.data?.message || batchErr.message}`);
    }
  } catch (error) {
    console.error('Error getting presigned download URLs:', error);
    throw new Error(`Failed to get presigned download URLs: ${error.response?.data?.message || error.message}`);
  }
};

/**
 * Convert an S3 key to a display URL using presigned download endpoint
 * Useful for displaying images that are stored as S3 keys in the database
 * @param {string} s3Key - S3 key of the file
 * @param {Function} axiosInstance - Axios instance with auth headers
 * @returns {Promise<string>} - Presigned download URL
 */
export const getDisplayUrlFromS3Key = async (s3Key, axiosInstance) => {
  try {
    const result = await getPresignedDownloadUrl(s3Key, axiosInstance);
    return result.download_url;
  } catch (error) {
    console.error('Error converting S3 key to display URL:', error);
    throw error;
  }
};

/**
 * Convert multiple S3 keys to display URLs using batch presigned download endpoint
 * @param {string[]} s3Keys - Array of S3 keys
 * @param {Function} axiosInstance - Axios instance with auth headers
 * @returns {Promise<string[]>} - Array of presigned download URLs in same order as s3Keys
 */
export const getDisplayUrlsFromS3Keys = async (s3Keys, axiosInstance) => {
  try {
    const results = await getPresignedDownloadUrls(s3Keys, axiosInstance);
    return results.map(result => result.download_url);
  } catch (error) {
    console.error('Error converting S3 keys to display URLs:', error);
    throw error;
  }
};